import React, { useEffect, useRef, useState } from 'react';
import { Crown, Check, Clock, Mic, CreditCard, Gift } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useSubscription, SUBSCRIPTION_TIERS, SubscriptionTier } from '@/hooks/useSubscription';
import { LoginModal } from '@/components/LoginModal';
import { Footer } from '@/components/Footer';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '@/utils/analytics';
import { useOctOffer, offerShortLine } from '@/services/offerApi';

const PlansPage: React.FC = () => {
  const { startSubscription, activateCreator, loading, isSubscriber, tier, fetchSubscription, error: checkoutError } = useSubscription();
  const [showLoginModal, setShowLoginModal] = useState(false);
  // A code can arrive in the URL — /plans?promo=houston-2026 is what a booth QR
  // code points at. Without this the link only put someone in front of an empty
  // box and asked them to type the thing they had just clicked.
  const promoFromUrl = (new URLSearchParams(window.location.search).get('promo') || '').trim();
  const [promoCode, setPromoCode] = useState(promoFromUrl);
  const [promoLoading, setPromoLoading] = useState(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const { toast } = useToast();
  const navigate = useNavigate();
  // Yearly offer: while it is open, the yearly plan carries today's credit for
  // anyone without paid Plus, so yearly is shown first and its button goes to /offer
  // (which keeps ?returnTo, ?c and ?p, so join-from-an-event attribution survives).
  const octOffer = useOctOffer();
  const offerForNew = !!octOffer?.open && (octOffer.kind === 'new' || octOffer.kind === 'signed_out');
  // A monthly member (or one on a trial) moving to yearly switches the plan they have,
  // on the offer page. A second checkout is refused server-side, and that refusal used
  // to vanish: the button just did nothing (Clayton Castle, Oct 16 2026).
  const offerForMonthly = !!octOffer?.open && octOffer.kind === 'monthly';
  useEffect(() => { if (offerForNew) setBillingInterval('yearly'); }, [offerForNew]);
  // Never fail silently: whatever stopped the checkout is said on screen.
  useEffect(() => {
    if (!checkoutError) return;
    const already = /already have an active subscription/i.test(checkoutError);
    toast({
      title: already ? 'You already have Hey Joe Plus' : 'Checkout did not start',
      description: already ? 'To move to the yearly plan, use the switch on the offer page: plus.heyjoe.io/offer' : checkoutError,
      variant: already ? undefined : 'destructive',
    });
  }, [checkoutError]);

  const isAuthenticated = !!(
    localStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    localStorage.getItem('castingUser')
  );

  // Auto-apply a code that came from a link, once, and only for someone signed in
  // — a signed-out visitor gets the login modal from the same handler instead, and
  // the code is still sitting in the box when they come back.
  const autoApplied = useRef(false);
  useEffect(() => {
    if (!promoFromUrl || autoApplied.current || !isAuthenticated || isSubscriber) return;
    autoApplied.current = true;
    handleRedeemPromo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoFromUrl, isAuthenticated, isSubscriber]);

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) return;
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    setPromoLoading(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const res = await fetch('/api/subscription/redeem-promo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ promoCode: promoCode.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to redeem promo code');

      // Redirect to Stripe checkout (collects CC for free trial)
      if (data.checkoutUrl) {
        toast({ title: 'Starting free trial...', description: data.message });
        window.location.href = data.checkoutUrl;
        return;
      }

      toast({ title: 'Promo code applied!', description: data.message });
      fetchSubscription();
      navigate('/audition?subscription=success');
    } catch (err: any) {
      toast({ title: 'Promo code error', description: err.message, variant: 'destructive' });
    } finally {
      setPromoLoading(false);
    }
  };

  const handleSelectPlan = async (selectedTier: SubscriptionTier) => {
    if (selectedTier === 'yearly' && (offerForNew || offerForMonthly)) { navigate(`/offer${window.location.search}`); return; }
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    trackEvent('select_plan', { plan: selectedTier });
    // Creator is free — grant it directly (no Stripe) and send them to their store.
    if (selectedTier === 'creator') {
      const ok = await activateCreator();
      if (ok) window.location.assign('/my-store');
      return;
    }
    // Carry a ?returnTo=/course/… through checkout so join-Plus-from-a-class lands
    // back on that class (where they can then register free). Also carry the ?c=
    // campaign code so the signup is attributed (e.g. david-webinar-joinplus).
    const params = new URLSearchParams(window.location.search);
    const returnTo = params.get('returnTo') || undefined;
    const campaign = params.get('c') || undefined;
    const campaignProductId = params.get('p') || undefined; // exact product attribution
    startSubscription(selectedTier, returnTo, campaign, campaignProductId);
  };

  const plusTier = billingInterval === 'yearly' ? SUBSCRIPTION_TIERS.yearly : SUBSCRIPTION_TIERS.monthly;

  const tiers = [
    {
      ...SUBSCRIPTION_TIERS.day_pass,
      icon: Clock,
      popular: false,
      buttonText: 'Get Day Pass'
    },
    {
      ...plusTier,
      icon: Crown,
      popular: true,
      buttonText: billingInterval === 'yearly' ? 'Subscribe Yearly' : 'Subscribe'
    },
    {
      ...SUBSCRIPTION_TIERS.creator,
      icon: Mic,
      popular: false,
      buttonText: 'Start selling — Free'
    }
  ];

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}
    >
      <main className="mx-auto flex-1 w-full" style={{ maxWidth: 1100, padding: '0 16px 64px' }}>
        {/* Header */}
        <div className="text-center" style={{ padding: '56px 0 24px' }}>
          <span
            className="inline-flex items-center gap-2 mono-label"
            style={{
              padding: '5px 12px',
              borderRadius: 999,
              background: 'var(--hj-yellow-tint)',
              color: 'var(--hj-yellow-ink)',
              fontWeight: 700,
              marginBottom: 18,
            }}
          >
            <Crown className="w-3.5 h-3.5" />
            Hey Joe+
          </span>
          <h1
            className="font-hj-display m-0"
            style={{
              fontSize: 'clamp(40px, 6vw, 64px)',
              lineHeight: 0.96,
              color: 'var(--hj-ink)',
              letterSpacing: '-0.035em',
              fontWeight: 800,
            }}
          >
            Pick your{' '}
            <span
              style={{
                position: 'relative',
                display: 'inline-block',
                padding: '0.04em 0.18em',
                background: 'var(--hj-yellow)',
                color: 'oklch(0.18 0.005 70)',
                clipPath: 'polygon(2% 8%, 98% 0%, 100% 92%, 0% 100%)',
                transform: 'rotate(-1.5deg)',
              }}
            >
              plan.
            </span>
          </h1>
          <p
            className="mx-auto m-0"
            style={{
              marginTop: 18,
              color: 'var(--hj-ink)',
              fontSize: 15.5,
              maxWidth: 600,
              lineHeight: 1.5,
            }}
          >
            Professional video tools, reader sessions, and AI enhancements for working actors.
          </p>
        </div>

        {/* Already Subscribed */}
        {isSubscriber && (
          <div
            className="text-center"
            style={{
              padding: 14,
              borderRadius: 'var(--hj-r-md)',
              background: 'oklch(0.42 0.15 145 / 0.12)',
              border: '1px solid oklch(0.55 0.16 145)',
              color: 'var(--hj-ink)',
              fontSize: 14,
              fontWeight: 500,
              marginBottom: 24,
            }}
          >
            You're currently subscribed to {SUBSCRIPTION_TIERS[tier!]?.name}.
          </div>
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center" style={{ marginBottom: 28 }}>
          <div
            className="inline-flex items-center gap-1"
            style={{
              padding: 4,
              borderRadius: 999,
              background: 'var(--hj-surface-1)',
              border: '1px solid var(--hj-hairline)',
            }}
          >
            <TogglePill
              active={billingInterval === 'monthly'}
              onClick={() => setBillingInterval('monthly')}
            >
              Monthly
            </TogglePill>
            <TogglePill
              active={billingInterval === 'yearly'}
              onClick={() => setBillingInterval('yearly')}
            >
              Yearly
              <span
                className="mono-label"
                style={{
                  marginLeft: 6,
                  color: billingInterval === 'yearly' ? 'oklch(0.18 0.005 70)' : 'var(--hj-green)',
                  fontWeight: 700,
                }}
              >
                Save 17%
              </span>
            </TogglePill>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" style={{ marginBottom: 40 }}>
          {tiers.map((tierItem) => {
            const Icon = tierItem.icon;
            const isCurrentTier = tier === tierItem.id;
            return (
              <div
                key={tierItem.id}
                className="relative flex flex-col"
                style={{
                  padding: 24,
                  borderRadius: 'var(--hj-r-md)',
                  background: tierItem.popular ? 'var(--hj-blue-tint)' : 'var(--hj-surface-1)',
                  border: `1px solid ${tierItem.popular ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
                  boxShadow: isCurrentTier ? '0 0 0 2px var(--hj-green) inset' : undefined,
                }}
              >
                {tierItem.popular && (
                  <span
                    className="mono-label absolute"
                    style={{
                      top: -10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: 'var(--hj-blue)',
                      color: 'oklch(0.18 0.005 70)',
                      fontWeight: 700,
                    }}
                  >
                    Most popular
                  </span>
                )}
                {isCurrentTier && (
                  <span
                    className="mono-label absolute"
                    style={{
                      top: -10,
                      right: 16,
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: 'var(--hj-green)',
                      color: 'oklch(0.18 0.005 70)',
                      fontWeight: 700,
                    }}
                  >
                    Current plan
                  </span>
                )}

                <div className="text-center" style={{ marginBottom: 18 }}>
                  <Icon
                    className="h-9 w-9 mx-auto"
                    style={{
                      color: tierItem.popular ? 'var(--hj-blue)' : 'var(--hj-ink-3)',
                      marginBottom: 12,
                    }}
                  />
                  <h3
                    className="font-hj-display m-0"
                    style={{
                      fontSize: 22,
                      color: 'var(--hj-ink)',
                      letterSpacing: '-0.02em',
                      fontWeight: 700,
                    }}
                  >
                    {tierItem.id === 'yearly' ? 'Hey Joe Plus' : tierItem.name}
                  </h3>
                  <div style={{ marginTop: 10 }}>
                    <span
                      className="tabular"
                      style={{
                        fontSize: 38,
                        fontWeight: 800,
                        color: 'var(--hj-ink)',
                        fontFamily: 'var(--hj-font-mono)',
                      }}
                    >
                      {tierItem.priceDisplay}
                    </span>
                    <span style={{ color: 'var(--hj-ink-3)', fontSize: 14 }}>
                      {' '}/{tierItem.period}
                    </span>
                  </div>
                  {'monthlyEquivalent' in tierItem && (
                    <p
                      className="m-0"
                      style={{ marginTop: 4, fontSize: 12.5, color: 'var(--hj-green)', fontWeight: 600 }}
                    >
                      {tierItem.monthlyEquivalent} — save $20 vs monthly
                    </p>
                  )}
                  {tierItem.id === 'yearly' && offerForNew && (
                    <p className="m-0" style={{ marginTop: 8, fontSize: 13.5, color: 'var(--hj-ink)', fontWeight: 700, lineHeight: 1.4 }}>
                      {offerShortLine(octOffer)}
                    </p>
                  )}
                </div>

                <p
                  className="text-center m-0"
                  style={{ fontSize: 13.5, color: 'var(--hj-ink-2)', lineHeight: 1.5, marginBottom: 20 }}
                >
                  {tierItem.description}
                </p>

                <ul className="flex-1 flex flex-col gap-2" style={{ paddingLeft: 0, listStyle: 'none', margin: 0, marginBottom: 22 }}>
                  {tierItem.features.map((feature, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2.5"
                      style={{ fontSize: 13.5, color: 'var(--hj-ink-2)', lineHeight: 1.45 }}
                    >
                      <Check className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--hj-green)', marginTop: 2 }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => handleSelectPlan(tierItem.id)}
                  disabled={loading || isCurrentTier}
                  className="inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed"
                  style={{
                    width: '100%',
                    padding: '12px 18px',
                    borderRadius: 999,
                    background: tierItem.popular ? 'var(--hj-blue)' : 'transparent',
                    border: `1px solid ${tierItem.popular ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
                    color: tierItem.popular ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink)',
                    fontSize: 14,
                    fontWeight: tierItem.popular ? 700 : 500,
                    fontFamily: 'inherit',
                    cursor: loading || isCurrentTier ? 'not-allowed' : 'pointer',
                    opacity: loading || isCurrentTier ? 0.6 : 1,
                    minHeight: 44,
                  }}
                >
                  {isCurrentTier ? 'Current plan' : tierItem.buttonText}
                </button>
              </div>
            );
          })}
        </div>

        {/* Promo Code */}
        <div className="mx-auto" style={{ maxWidth: 460, marginBottom: 48 }}>
          <div className="flex items-center justify-center gap-2" style={{ marginBottom: 10 }}>
            <Gift className="w-3.5 h-3.5" style={{ color: 'var(--hj-ink-4)' }} />
            <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Got a promo code?</span>
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Enter promo code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRedeemPromo()}
              className="flex-1 min-w-0"
            />
            <button
              type="button"
              onClick={handleRedeemPromo}
              disabled={promoLoading || !promoCode.trim()}
              className="disabled:cursor-not-allowed"
              style={{
                padding: '0 18px',
                borderRadius: 999,
                background: 'transparent',
                border: '1px solid var(--hj-hairline)',
                color: 'var(--hj-ink)',
                fontSize: 13,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: promoLoading || !promoCode.trim() ? 'not-allowed' : 'pointer',
                opacity: promoLoading || !promoCode.trim() ? 0.5 : 1,
                minHeight: 40,
              }}
            >
              {promoLoading ? 'Applying…' : 'Apply'}
            </button>
          </div>
        </div>

        {/* Trust */}
        <div
          className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3"
          style={{
            padding: '24px 0 8px',
            borderTop: '1px solid var(--hj-hairline-soft)',
            fontSize: 12.5,
            color: 'var(--hj-ink-3)',
          }}
        >
          <span className="inline-flex items-center gap-1.5">
            <Check className="h-3.5 w-3.5" style={{ color: 'var(--hj-green)' }} />
            Cancel anytime
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CreditCard className="h-3.5 w-3.5" />
            Secure Stripe checkout
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            Instant access
          </span>
        </div>
      </main>

      <Footer />

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={() => setShowLoginModal(false)}
      />
    </div>
  );
};

const TogglePill: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center transition-all"
    style={{
      padding: '7px 16px',
      borderRadius: 999,
      background: active ? 'var(--hj-blue)' : 'transparent',
      color: active ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink-3)',
      fontSize: 13,
      fontWeight: active ? 700 : 500,
      fontFamily: 'inherit',
      cursor: 'pointer',
      border: 0,
    }}
  >
    {children}
  </button>
);

export default PlansPage;
